/**
 * @file NativeChat.js
 * @author Simon Tenedero, Jonas Matulis
 * @created 2024-XX-XX
 * @lastModified 2025-05-28
 * @description file containing NativeChat component
 */

import React, { useState, useRef, useEffect } from "react";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import Messages from "../Chat/Messages";
import ChatInputForm from "../Chat/ChatInputForm";
import UserInfoModal from "../Modal/UserInfoModal";
import { fetchVideoDetails } from "../../utils/livestreamsUtils";
import data from "@emoji-mart/data";
import { useUser } from "../../context/UserContext";
import { fetchPublicUserInfo, logWebsiteUsage } from "../../utils/usersUtils";

/**
 * NOTE: This component is currently commented out and not in use. (to be replced with ClubverseChat.js)
 *
 * @param {Object} props - The component properties
 * @param {string} props.videoId - ID of the livestream video.
 * @param {Array} props.activeUsers - List of users currently active in the chat.
 *
 * @returns {JSX.Element} The rendered NativeChat component.
 */
const NativeChat = ({ videoId, activeUsers }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [showOptions, setShowOptions] = useState({});
  const [selectedUser, setSelectedUser] = useState(null);
  const [mentionDropdown, setMentionDropdown] = useState([]);
  const [videoTitle, setVideoTitle] = useState("");
  const messagesEndRef = useRef(null);
  const [isPopupOpen, setIsPopupOpen] = useState(true);

  const { user } = useUser();

  useEffect(() => {
    if (videoId) {
      // Listen for new messages
      const q = query(
        collection(db, "livestreams", videoId, "messages"),
        orderBy("timestamp", "asc"),
      );
      const unsubscribe = onSnapshot(q, async (snapshot) => {
        const messagesData = await Promise.all(
          snapshot.docs.map(async (docSnapshot) => {
            const message = docSnapshot.data();
            if (message.authorUid) {
              const userData = await fetchPublicUserInfo(message.authorUid);
              if (userData) {
                return {
                  ...message,
                  messageId: docSnapshot.id,
                  authorPhoto: userData.profilePicture,
                  authorName: userData.username,
                  userInfo: userData,
                };
              }
            }
            return message;
          }),
        );

        setMessages(messagesData);
      });

      // Fetch video title
      const fetchVideoTitle = async () => {
        const videoDetails = await fetchVideoDetails(videoId);
        console.log(videoDetails);
        if (videoDetails) {
          setVideoTitle(videoDetails.title);
        }
      };

      fetchVideoTitle();

      // Cleanup listener on component unmount
      return () => unsubscribe();
    }
  }, [videoId]);

  /**
   * Handles sending a new message.
   * @param {React.FormEvent} e - Form submit event.
   */
  const handleSendClick = async (e) => {
    e.preventDefault();

    const currentTime = new Date();
    if (input.trim() && videoId && user) {
      const convertedText = convertEmojiCodes(input);
      const messageData = {
        text: convertedText,
        authorUid: user.uid,
        timestamp: currentTime.toISOString(), // need this to sort messages by time
        sentAt: currentTime,
      };
      const messageRef = await addDoc(
        collection(db, "livestreams", videoId, "messages"),
        messageData,
      );
      setInput("");

      logWebsiteUsage(
        user.uid,
        `sent message with ID '${messageRef.id}' in livestream with ID '${videoId}'`,
        "messageActivity",
        currentTime,
      );
    }
  };

  /**
   * Converts emoji shortcodes in the text to native emojis.
   * @param {string} text - Text containing emoji shortcodes.
   * @returns {string} Text with native emojis.
   */
  const convertEmojiCodes = (text) => {
    const emojiMap = data.emojis;
    let result = text;
    Object.keys(emojiMap).forEach((code) => {
      const emoji = emojiMap[code];
      if (result.includes(`:${code}:`))
        result = result.replace(`:${code}:`, emoji.skins[0].native);
    });
    return result;
  };

  /**
   * Formats timestamp into a readable time string.
   * @param {string} timestamp - The timestamp to format.
   * @returns {string} Formatted time string.
   */
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  /**
   * Closes options menu if click is outside the menu.
   * @param {MouseEvent} event - The click event.
   */
  const handleClickOutside = (event) => {
    if (!event.target.closest(".options-menu")) {
      setShowOptions({});
    }
  };

  /**
   * Fetches user data and sets it for the UserInfoModal.
   * @param {string} uid - User ID to fetch information for.
   */
  const handleSeeAccountInfo = async (uid) => {
    if (!uid) return;
    try {
      const userRef = doc(db, "publicUserInfo", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setSelectedUser(userSnap.data());
      }
    } catch (error) {
      console.error("Failed to fetch user data:", error);
    }
  };

  /**
   * Handles changes to the chat input field and updates mention suggestions.
   * @param {React.ChangeEvent<HTMLInputElement>} e - Input change event.
   */
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);

    if (value.includes("@")) {
      const mentionPart = value.split("@").pop().toLowerCase();
      if (mentionPart) {
        const filteredUsers = activeUsers.filter((user) =>
          user.username.toLowerCase().includes(mentionPart),
        );
        setMentionDropdown(filteredUsers);
      } else {
        setMentionDropdown([]);
      }
    } else {
      setMentionDropdown([]);
    }
  };

  /**
   * Inserts mention into the input field.
   * @param {string} username - Username of the mentioned user.
   */
  const handleMentionClick = (username) => {
    const inputParts = input.split("@");
    inputParts.pop();
    const newValue = `${inputParts.join("@")}@${username} `;
    setInput(newValue);
    setMentionDropdown([]);
  };

  // Attach and detach click event listener for handling clicks outside options menu
  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative flex flex-col w-full h-full bg-neutral rounded-xl">
      <div className="px-8 py-2 rounded-lg shadow-lg text-center items-center">
        <h2 className="text-lg font-semibold text-base">native chat</h2>
        <span className="w-full whitespace-nowrap overflow-hidden text-ellipsis block text-sm">
          {videoTitle}
        </span>
      </div>

      <div className="flex-grow overflow-y-scroll">
        <Messages
          privacyLevel="public"
          chatId={videoId}
          messages={messages}
          user={user}
          formatTimestamp={formatTimestamp}
          handleSeeAccountInfo={handleSeeAccountInfo}
          isPopupOpen={isPopupOpen}
          setIsPopupOpen={setIsPopupOpen}
        />
        <div ref={messagesEndRef} />
      </div>
      <ChatInputForm
        input={input}
        handleInputChange={handleInputChange}
        handleSendClick={handleSendClick}
        mentionDropdown={mentionDropdown}
        handleMentionClick={handleMentionClick}
      />
      <UserInfoModal
        selectedUser={selectedUser}
        setSelectedUser={setSelectedUser}
      />
    </div>
  );
};

export default NativeChat;
